/**
 * Utility functions for calculating player selection odds
 * Based on the player selection process documented in PlayerSelectionExplained.md
 *
 * Visual Selection Point Indicators (Added 2025-10-31):
 * Players in random selection zone display colored circles (●) showing their selection points.
 * - Selection points = 1 (base) + benchWarmerStreak (bonus for consecutive reserve games)
 * - Example: Player with 2 consecutive reserve games = 1 + 2 = 3 points = ●●●
 * - Color coding: Blue (85%+ odds), Yellow (50-84% odds), Red (<50% odds)
 * - Used in RegisteredPlayerGrid.tsx and RegisteredPlayerListView.tsx
 *
 * Related Fix (2025-10-31):
 * Fixed calculate_bench_warmer_streak() database function that was only returning 1/0
 * instead of counting consecutive reserve appearances. See BenchWarmerStreakCalculationFix.md.
 */

export interface PlayerOdds {
  percentage: number;
  status: 'guaranteed' | 'merit' | 'random' | 'unlikely';
  description: string;
}

interface PlayerForOdds {
  id: string;
}

interface RegistrationForOdds {
  player: PlayerForOdds;
  using_token?: boolean;
}

interface PlayerStatsForOdds {
  benchWarmerStreak?: number;
  xp?: number;
  rank?: number;
}

/**
 * Calculate exact probability of selection using recursive approach
 * Considers all possible selection sequences for weighted selection without replacement
 *
 * @param targetPlayerId - ID of player to calculate probability for
 * @param playerPoints - Map of player IDs to their selection weights
 * @param slotsRemaining - Number of slots still to be filled
 * @returns Probability of selection (0-1)
 */
function calculateExactProbability(
  targetPlayerId: string,
  playerPoints: Map<string, number>,
  slotsRemaining: number
): number {
  if (slotsRemaining === 0 || playerPoints.size === 0) {
    return 0;
  }

  const totalPoints = Array.from(playerPoints.values()).reduce((sum, p) => sum + p, 0);
  const targetPoints = playerPoints.get(targetPlayerId) || 0;

  if (targetPoints === 0) {
    return 0;
  }

  // Probability of being selected in this round
  const probSelectedNow = targetPoints / totalPoints;

  // If only one slot left or this is the last player, return current probability
  if (slotsRemaining === 1 || playerPoints.size === 1) {
    return probSelectedNow;
  }

  // Probability of NOT being selected in this round, but selected in future rounds
  // For each other player that could be selected, calculate probability they're selected
  // and then calculate our probability in the remaining rounds
  let probSelectedLater = 0;

  playerPoints.forEach((points, playerId) => {
    if (playerId !== targetPlayerId) {
      const probThisPlayerSelected = points / totalPoints;

      // Create new map without this player
      const remainingPlayers = new Map(playerPoints);
      remainingPlayers.delete(playerId);

      // Recursively calculate probability in remaining rounds
      const probInFutureRounds = calculateExactProbability(
        targetPlayerId,
        remainingPlayers,
        slotsRemaining - 1
      );

      probSelectedLater += probThisPlayerSelected * probInFutureRounds;
    }
  });

  return probSelectedNow + probSelectedLater;
}

/**
 * Calculate selection odds for each player in the registration list
 *
 * @param sortedRegistrations - Players sorted by selection priority (tokens first, then XP)
 * @param playerStats - Stats for each player including bench warmer streak, XP, and rank
 * @param tokenCooldownPlayerIds - Set of player IDs on token cooldown
 * @param xpSlots - Number of slots for merit-based selection
 * @param randomSlots - Number of slots for random selection
 * @param maxPlayers - Maximum number of players in the game
 * @param unregisteredTokenHoldersCount - Number of players with available tokens who haven't registered yet
 * @param unregisteredPlayersXP - Array of XP values for unregistered players (sorted descending)
 * @param noShowPenaltyPlayerIds - Set of player IDs on the no-show penalty (last of all, below token cooldown)
 * @returns Map of player ID to their selection odds
 */
export function calculateSelectionOdds(
  sortedRegistrations: Array<RegistrationForOdds>,
  playerStats: Record<string, PlayerStatsForOdds>,
  tokenCooldownPlayerIds: Set<string>,
  xpSlots: number,
  randomSlots: number,
  maxPlayers: number,
  unregisteredTokenHoldersCount: number = 0,
  unregisteredPlayersXP: number[] = [],
  noShowPenaltyPlayerIds: Set<string> = new Set()
): Map<string, PlayerOdds> {
  const oddsMap = new Map<string, PlayerOdds>();

  // Check if there's a randomiser (only when registrations exceed max players)
  const hasRandomiser = sortedRegistrations.length > maxPlayers;

  // Everyone gets in with no threats - early return
  if (!hasRandomiser && unregisteredTokenHoldersCount === 0 && unregisteredPlayersXP.length === 0) {
    sortedRegistrations.forEach(reg => {
      oddsMap.set(reg.player.id, {
        percentage: 100,
        status: 'guaranteed',
        description: 'Everyone plays when registrations ≤ ' + maxPlayers
      });
    });
    return oddsMap;
  }

  // Calculate odds for all registered players (same logic regardless of randomiser)
  sortedRegistrations.forEach((reg, index) => {
    const playerId = reg.player.id;
    const playerXP = playerStats[playerId]?.xp || 0;

    // Token users are guaranteed
    if (reg.using_token) {
      oddsMap.set(playerId, {
        percentage: 100,
        status: 'guaranteed',
        description: 'Priority Token - Guaranteed slot'
      });
      return;
    }

    // Count how many unregistered players have higher XP than this player
    const higherXPUnregistered = unregisteredPlayersXP.filter(xp => xp > playerXP).length;

    // Tokens reduce available XP slots when used
    const effectiveXpSlots = xpSlots - unregisteredTokenHoldersCount;

    // Calculate how many spots down they could be pushed (only XP affects position)
    const wouldBeAtPosition = index + higherXPUnregistered;

    // Merit-based players (currently within XP slots)
    if (index < xpSlots) {
      // Completely safe - even with all risks, still in merit zone
      if (wouldBeAtPosition < effectiveXpSlots) {
        oddsMap.set(playerId, {
          percentage: 100,
          status: 'merit',
          description: 'Merit Selection - Safe from unregistered players'
        });
        return;
      }

      // Would be pushed into random zone (or out entirely if no randomiser) if risks materialize
      if (wouldBeAtPosition >= effectiveXpSlots) {
        // Calculate probability based on registration likelihood
        // Assume 40% chance each higher-XP player registers
        // Assume 50% chance each token holder registers and uses token
        const expectedHigherXPRegistrations = higherXPUnregistered * 0.4;
        const expectedTokenUsage = unregisteredTokenHoldersCount * 0.5;
        const expectedPushDown = expectedHigherXPRegistrations;
        const expectedXpSlots = xpSlots - (unregisteredTokenHoldersCount * 0.5);
        const likelyPosition = index + expectedPushDown;

        // Still likely in merit zone
        if (likelyPosition < expectedXpSlots - 1) {
          const riskDescription = [];
          if (higherXPUnregistered > 0) riskDescription.push(`${higherXPUnregistered} higher-XP player${higherXPUnregistered > 1 ? 's' : ''}`);
          if (unregisteredTokenHoldersCount > 0) riskDescription.push(`${unregisteredTokenHoldersCount} token holder${unregisteredTokenHoldersCount > 1 ? 's' : ''}`);

          oddsMap.set(playerId, {
            percentage: 85,
            status: 'merit',
            description: `Merit Selection - Likely safe (at risk from ${riskDescription.join(' and ')})`
          });
          return;
        }

        // Borderline - could go either way
        const riskDescription = [];
        if (higherXPUnregistered > 0) riskDescription.push(`${higherXPUnregistered} higher-XP player${higherXPUnregistered > 1 ? 's' : ''}`);
        if (unregisteredTokenHoldersCount > 0) riskDescription.push(`${unregisteredTokenHoldersCount} token holder${unregisteredTokenHoldersCount > 1 ? 's' : ''}`);

        oddsMap.set(playerId, {
          percentage: 60,
          status: 'merit',
          description: `Merit Selection - At risk from ${riskDescription.join(' and ')}`
        });
        return;
      }
    }

    // Players outside XP slots when there's NO randomiser (≤18 players)
    // These players are at risk even though registrations haven't exceeded max
    if (index >= xpSlots && !hasRandomiser) {
      // If there are threats, they're at high risk of being pushed out
      if (unregisteredTokenHoldersCount > 0 || higherXPUnregistered > 0) {
        const riskDescription = [];
        if (higherXPUnregistered > 0) riskDescription.push(`${higherXPUnregistered} higher-XP player${higherXPUnregistered > 1 ? 's' : ''}`);
        if (unregisteredTokenHoldersCount > 0) riskDescription.push(`${unregisteredTokenHoldersCount} token holder${unregisteredTokenHoldersCount > 1 ? 's' : ''}`);

        oddsMap.set(playerId, {
          percentage: 40,
          status: 'merit',
          description: `Random Zone - High risk from ${riskDescription.join(' and ')}`
        });
      } else {
        // No threats but still in random zone - will play if no late registrations
        oddsMap.set(playerId, {
          percentage: 85,
          status: 'merit',
          description: `Random Zone - Will play unless late registrations arrive`
        });
      }
    }
  });

  // Calculate odds for random selection zone (only when there is a randomiser)
  if (!hasRandomiser) {
    return oddsMap;
  }

  const randomZonePlayers = sortedRegistrations.slice(xpSlots);

  if (randomZonePlayers.length === 0) {
    return oddsMap;
  }

  // Separate players by token cooldown and no-show penalty status
  // Token cooldown players are only considered AFTER all other players, and
  // no-show penalty players after them (the penalty wins over the cooldown)
  const noShowPenaltyPlayers = randomZonePlayers.filter(reg =>
    noShowPenaltyPlayerIds.has(reg.player.id)
  );
  const tokenCooldownPlayers = randomZonePlayers.filter(reg =>
    tokenCooldownPlayerIds.has(reg.player.id) && !noShowPenaltyPlayerIds.has(reg.player.id)
  );
  const eligiblePlayers = randomZonePlayers.filter(reg =>
    !tokenCooldownPlayerIds.has(reg.player.id) && !noShowPenaltyPlayerIds.has(reg.player.id)
  );

  // Calculate if token cooldown players have any chance
  // They're only selected if there aren't enough eligible players
  const hasTokenCooldownChance = eligiblePlayers.length < randomSlots;

  // Calculate odds for eligible (non-cooldown) players
  if (eligiblePlayers.length > 0) {
    const playerPoints = new Map<string, number>();
    let totalPoints = 0;

    eligiblePlayers.forEach(reg => {
      const benchWarmerStreak = playerStats[reg.player.id]?.benchWarmerStreak || 0;
      const points = 1 + benchWarmerStreak; // Base 1 + streak bonus
      playerPoints.set(reg.player.id, points);
      totalPoints += points;
    });

    // Calculate exact probabilities using recursive approach
    eligiblePlayers.forEach(reg => {
      const playerId = reg.player.id;

      // Calculate probability recursively considering all possible selection sequences
      const probability = calculateExactProbability(
        playerId,
        playerPoints,
        randomSlots
      );

      const percentage = Math.min(100, Math.round(probability * 100));
      const benchWarmerStreak = playerStats[playerId]?.benchWarmerStreak || 0;

      oddsMap.set(playerId, {
        percentage,
        status: 'random',
        description: benchWarmerStreak > 0
          ? `Random Selection - ${benchWarmerStreak} game${benchWarmerStreak > 1 ? 's' : ''} as reserve boosts odds`
          : 'Random Selection - Base odds'
      });
    });
  }

  // Calculate odds for token cooldown players
  tokenCooldownPlayers.forEach(reg => {
    const playerId = reg.player.id;

    if (!hasTokenCooldownChance) {
      // No chance - there are enough eligible players to fill all slots
      oddsMap.set(playerId, {
        percentage: 0,
        status: 'random',
        description: 'Token Cooldown - Last priority in random selection'
      });
    } else {
      // Has a chance - calculate based on remaining slots after eligible players
      const remainingSlots = randomSlots - eligiblePlayers.length;
      const playerPoints = new Map<string, number>();

      tokenCooldownPlayers.forEach(cooldownReg => {
        const benchWarmerStreak = playerStats[cooldownReg.player.id]?.benchWarmerStreak || 0;
        const points = 1 + benchWarmerStreak;
        playerPoints.set(cooldownReg.player.id, points);
      });

      // Calculate exact probability using recursive approach
      const probability = calculateExactProbability(
        playerId,
        playerPoints,
        remainingSlots
      );

      const percentage = Math.min(100, Math.round(probability * 100));
      const benchWarmerStreak = playerStats[playerId]?.benchWarmerStreak || 0;

      oddsMap.set(playerId, {
        percentage,
        status: 'random',
        description: `Token Cooldown - Only selected if needed (${benchWarmerStreak} reserve streak${benchWarmerStreak !== 1 ? 's' : ''})`
      });
    }
  });

  // Calculate odds for no-show penalty players: only if slots are left after
  // everyone else, including token cooldown players
  const penaltySlots = randomSlots - eligiblePlayers.length - tokenCooldownPlayers.length;
  noShowPenaltyPlayers.forEach(reg => {
    const playerId = reg.player.id;

    if (penaltySlots <= 0) {
      oddsMap.set(playerId, {
        percentage: 0,
        status: 'random',
        description: 'No-show penalty - Last priority in random selection'
      });
      return;
    }

    const playerPoints = new Map<string, number>();
    noShowPenaltyPlayers.forEach(penaltyReg => {
      const benchWarmerStreak = playerStats[penaltyReg.player.id]?.benchWarmerStreak || 0;
      playerPoints.set(penaltyReg.player.id, 1 + benchWarmerStreak);
    });

    const probability = calculateExactProbability(playerId, playerPoints, penaltySlots);
    oddsMap.set(playerId, {
      percentage: Math.min(100, Math.round(probability * 100)),
      status: 'random',
      description: 'No-show penalty - Only selected if needed'
    });
  });

  return oddsMap;
}

/**
 * Format odds for display
 * Only shows percentages for random zone players
 */
export function formatOdds(odds: PlayerOdds): string {
  // Only show percentages for random selection zone
  if (odds.status === 'random') {
    return `${odds.percentage}%`;
  }

  // For merit and guaranteed, just show status
  if (odds.percentage === 100) {
    return 'Guaranteed';
  }

  // For merit zone players with < 100% odds, show status instead of percentage
  return 'At Risk';
}

/**
 * Get color class for odds display
 */
export function getOddsColorClass(odds: PlayerOdds): string {
  if (odds.percentage === 100) {
    return 'text-success';
  }
  if (odds.percentage >= 85) {
    return 'text-success';
  }
  if (odds.percentage >= 50) {
    return 'text-warning';
  }
  return 'text-error';
}
